"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function ClientSuccessStories() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const testimonials = [{
    author: "James Henderson",
    location: "Fort Worth",
    quote: "Arbor Pro handled our massive oak removal with surgical precision.",
    body: "After the last storm, we had a dangerous limb hanging over our roof. The team arrived on time, used specialized rigging to protect our landscaping, and left the yard cleaner than they found it. Truly the gold standard for DFW tree care.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/fort_worth_oak_service.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/fort_worth_oak_service.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/fort_worth_oak_service.webp-wvc-sizes"
  }, {
    author: "Sarah Miller",
    location: "Southlake",
    quote: "Their deep-root fertilization saved our ornamental trees during the drought.",
    body: "We were worried about losing our Japanese Maples. Arbor Pro's arborist diagnosed the nutrient deficiency and implemented a recovery plan. Six months later, the canopy is lush and vibrant. They are architects of the local landscape.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/southlake_tree_health.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/southlake_tree_health.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/southlake_tree_health.png-wvc-sizes"
  }, {
    author: "Robert Chen",
    location: "Arlington",
    quote: "Professional, insured, and incredibly knowledgeable about Texas species.",
    body: "It is rare to find a crew that understands the structural integrity of a tree rather than just 'lopping off branches.' They pruned our pecans for health and production. The technical expertise they bring is unmatched in North Texas.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arlington_pecan_pruning.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arlington_pecan_pruning.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arlington_pecan_pruning.png-wvc-sizes"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [testimonials.length]);
  const handlePrev = () => {
    setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const handleNext = () => {
    setActiveIndex(prev => (prev + 1) % testimonials.length);
  };
  const active = testimonials[activeIndex];
  return <section data-section-id="4496" id="client-success-stories" className="relative bg-background text-foreground py-24 md:py-32 lg:py-40 overflow-hidden border-t border-border">
      <div ref={ref} className={`mx-auto max-w-7xl px-6 lg:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-6" style={{
          color: "var(--ring)"
        }}>
            03 // Client Success Stories
          </p>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl text-foreground leading-[0.95] tracking-tighter">
            The Canopy Reputation.
          </h2>
        </div>

        {}
        <div className="relative max-w-4xl mx-auto">
          {}
          <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="hidden md:flex absolute -left-16 lg:-left-24 top-1/2 -translate-y-1/2 size-12 rounded-md hover:bg-accent hover:text-accent-foreground z-10 border border-border" style={{
          color: "var(--foreground)"
        }}>
            <ChevronLeft className="size-8" strokeWidth={1.5} />
          </Button>

          <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="hidden md:flex absolute -right-16 lg:-right-24 top-1/2 -translate-y-1/2 size-12 rounded-md hover:bg-accent hover:text-accent-foreground z-10 border border-border" style={{
          color: "var(--foreground)"
        }}>
            <ChevronRight className="size-8" strokeWidth={1.5} />
          </Button>

          {}
          <div className="relative min-h-[600px] md:min-h-[560px]">
            {testimonials.map((t, i) => <div data-index={i} key={i} className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${i === activeIndex ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                <div className="flex flex-col items-center text-center">
                  {}
                  <div className="relative mb-8 overflow-hidden grayscale" style={{
                width: "140px",
                height: "140px",
                borderRadius: "0px",
                padding: "0px",
                border: "1px solid var(--ring)"
              }}>
                    <img src={t.image} data-wvc-srcset={t.srcset} data-wvc-sizes={t.sizes} alt={`${t.author}'s property`} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
                  borderRadius: "0px"
                }} />
                  </div>

                  {}
                  <p className="font-mono text-sm md:text-base text-foreground mb-3 uppercase tracking-wider">
                    {t.author} — {t.location}
                  </p>

                  {}
                  <div className="flex items-center gap-1.5 mb-8">
                    {[0, 1, 2, 3, 4].map(s => <Star data-index={s} key={s} className="size-4" style={{
                  color: "var(--ring)",
                  fill: "var(--ring)"
                }} strokeWidth={1} />)}
                  </div>

                  {}
                  <div className="relative px-8 md:px-16 mb-8">
                    <span className="absolute -top-4 left-0 md:left-2 font-serif text-7xl md:text-8xl leading-none select-none" style={{
                  color: "var(--ring)",
                  opacity: 0.3
                }} aria-hidden="true">
                      &ldquo;
                    </span>
                    <blockquote className="font-default font-semibold text-2xl md:text-3xl lg:text-[2.25rem] leading-tight text-foreground max-w-2xl mx-auto tracking-tight">
                      {t.quote}
                    </blockquote>
                    <span className="absolute -bottom-12 right-0 md:right-2 font-serif text-7xl md:text-8xl leading-none select-none" style={{
                  color: "var(--ring)",
                  opacity: 0.3
                }} aria-hidden="true">
                      &rdquo;
                    </span>
                  </div>

                  {}
                  <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-[60ch] mx-auto px-4">
                    {t.body}
                  </p>
                </div>
              </div>)}
          </div>

          {}
          <div className="flex md:hidden items-center justify-center gap-6 mt-8">
            <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="size-10 rounded-md border border-border" style={{
            color: "var(--foreground)"
          }}>
              <ChevronLeft className="size-6" strokeWidth={1.5} />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="size-10 rounded-md border border-border" style={{
            color: "var(--foreground)"
          }}>
              <ChevronRight className="size-6" strokeWidth={1.5} />
            </Button>
          </div>

          {}
          <div className="flex items-center justify-center gap-3 mt-10 md:mt-12">
            {testimonials.map((_, i) => <Button data-index={i} key={i} onClick={() => setActiveIndex(i)} aria-label={`Go to testimonial ${i + 1}`} variant="ghost" className="p-0 hover:bg-transparent h-auto" style={{
            width: i === activeIndex ? "32px" : "8px",
            height: "2px",
            backgroundColor: i === activeIndex ? "var(--ring)" : "var(--border)",
            borderRadius: "0px",
            transition: "all 0.3s ease"
          }} />)}
          </div>

          {}
          <div className="mt-16 text-center flex flex-col items-center">
            <p className="font-default text-lg md:text-xl text-foreground mb-8 max-w-2xl">
              Join our satisfied DFW homeowners. Request your free estimate today.
            </p>
            <Button asChild size="lg" className="px-10 py-7 text-base font-mono font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 border border-primary">
              <Link to="/GetQuotePage">
                Get Your Quote
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}